import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../api/client';
import { toast } from 'sonner';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { 
  ArrowLeft, 
  Plus,
  Trash2,
  Save,
  Send,
  Paperclip,
  FileText,
  X
} from 'lucide-react';

const PurchaseOrderCreate = ({ onNavigate }) => {
  // Use onNavigate prop instead of react-router navigate
  const tenantId = localStorage.getItem('tenant_id') || 'tenant-123';
  
  // Form state
  const [formData, setFormData] = useState({
    po_number: generatePONumber(),
    vendor_po_number: '',  // Supplier's reference number for this order
    supplier_id: '',
    supplier_name: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_delivery: '',
    warehouse_location: 'Main Warehouse',
    currency: 'USD',
    status: 'draft',
    payment_terms: 'Net 30',
    notes: ''
  });

  // Line items state
  const [lineItems, setLineItems] = useState([]);
  
  // Data for dropdowns
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // ============================================================
  // PO-LEVEL COST FIELDS (Editable with default $0)
  // ============================================================
  const [poCosts, setPoCosts] = useState({
    intl_freight: 0,        // International Freight
    import_duties: 0,       // Import Duties
    inbound_handling: 0,    // Inbound Handling (was hardcoded $200)
    domestic_freight: 0     // Domestic Freight (was hardcoded $100)
  });
  
  // Handler for PO cost field changes
  const handlePoCostChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setPoCosts(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  // ============================================================
  // SUPPLIER SKU SEARCH: Async product search with supplier matching
  // ============================================================
  const loadProductOptions = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 1) {
      // Return initial product list when no search
      return products.slice(0, 50).map(product => ({
        value: product.sku,
        label: `${product.sku} - ${product.name || product.product_name}`,
        product: product
      }));
    }
    
    try {
      // Use the new search-for-po endpoint
      const params = { q: inputValue };
      if (formData.supplier_id) {
        params.supplier_id = formData.supplier_id;
      }
      
      const response = await api.get('/api/v1/product-master/search-for-po', { params });
      const searchResults = response.products || response.data?.products || [];
      
      return searchResults.map(product => {
        // If product was found via supplier SKU, highlight it
        const supplierMatch = product.supplier_sku_match;
        const label = supplierMatch 
          ? `${product.sku} (Supplier: ${supplierMatch}) - ${product.name || 'Unknown'}`
          : `${product.sku} - ${product.name || 'Unknown'}`;
        
        return {
          value: product.sku,
          label: label,
          product: product,
          supplierSku: supplierMatch,
          supplierCost: product.supplier_unit_cost
        };
      });
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  }, [formData.supplier_id, products]);

  // Natural sort comparator for alphanumeric strings
  const naturalSort = (a, b) => {
    const ax = [], bx = [];
    a.replace(/(\d+)|(\D+)/g, (_, $1, $2) => { ax.push([$1 || Infinity, $2 || ""]) });
    b.replace(/(\d+)|(\D+)/g, (_, $1, $2) => { bx.push([$1 || Infinity, $2 || ""]) });
    while(ax.length && bx.length) {
      const an = ax.shift();
      const bn = bx.shift();
      const nn = (parseInt(an[0]) - parseInt(bn[0])) || an[1].localeCompare(bn[1]);
      if(nn) return nn;
    }
    return ax.length - bx.length;
  };

  // Transform products for react-select with natural sorting
  const productOptions = useMemo(() => {
    const options = products.map(product => ({
      value: product.sku,
      label: `${product.sku} - ${product.name || product.product_name}`,
      product: product
    }));
    
    // Sort naturally by SKU
    return options.sort((a, b) => naturalSort(a.value, b.value));
  }, [products]);

  // Custom styles for react-select to match dark theme
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#0f1729',
      borderColor: state.isFocused ? '#3b82f6' : '#374151',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      minHeight: '32px',
      height: '32px',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      height: '30px',
      padding: '0 8px'
    }),
    input: (base) => ({
      ...base,
      margin: '0',
      padding: '0',
      color: 'white'
    }),
    singleValue: (base) => ({
      ...base,
      color: 'white',
      fontSize: '14px'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#9ca3af',
      fontSize: '14px'
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: '#0f1729',
      border: '1px solid #374151',
      zIndex: 50
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: '300px'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? '#1e3a5f' : '#0f1729',
      color: 'white',
      fontSize: '13px',
      padding: '8px 12px',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: '#1e3a5f'
      }
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: '4px',
      color: '#9ca3af'
    })
  };

  // Fetch products and suppliers on mount
  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
  }, []);

  // Generate PO number
  function generatePONumber() {
    const year = new Date().getFullYear();
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `PO-${year}-${random}`;
  }

  // Fetch products
  const fetchProducts = async () => {
    try {
      // api.get returns data directly (already unwrapped)
      const data = await api.get(`/api/v1/product-master/list?tenant_id=${tenantId}&page=1&page_size=500`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      
      console.log('Products API response:', data);
      
      // Handle different response formats
      let productsData = [];
      if (Array.isArray(data)) {
        productsData = data;
      } else if (data && data.products) {
        productsData = data.products;
      } else if (data && data.items) {
        productsData = data.items;
      }
      
      // ============================================================
      // SORT PRODUCTS BY SKU (NUMERIC FIRST, THEN ALPHABETIC)
      // ============================================================
      productsData.sort((a, b) => {
        const skuA = a.sku || '';
        const skuB = b.sku || '';
        
        // Extract numeric part of SKU for proper sorting
        const numA = parseInt(skuA.replace(/\D/g, ''), 10);
        const numB = parseInt(skuB.replace(/\D/g, ''), 10);
        
        // Both have numbers - sort numerically
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        
        // Only one has a number - numbers first
        if (!isNaN(numA)) return -1;
        if (!isNaN(numB)) return 1;
        
        // Neither has numbers - sort alphabetically
        return skuA.localeCompare(skuB);
      });
      
      console.log('Setting products (sorted):', productsData.length);
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  // Fetch suppliers
  const fetchSuppliers = async () => {
    try {
      // api.get returns data directly (already unwrapped)
      const data = await api.get(`/api/v1/suppliers`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      
      if (Array.isArray(data)) {
        // Map suppliers with id field for compatibility
        const mappedSuppliers = data.map(s => ({
          _id: s.id,
          id: s.id,
          name: s.name,
          code: s.code,
          payment_terms: s.payment_terms,
          currency: s.currency
        }));
        setSuppliers(mappedSuppliers);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  // Add product to line items
  const handleAddProduct = (productSku, selectedOption = null) => {
    const skuToAdd = productSku || (selectedProduct?.value);
    if (!skuToAdd) return;

    // Try to find product from selectedOption first (contains supplier data), then fall back to products list
    let product = selectedOption?.product || products.find(p => p.sku === skuToAdd);
    if (!product) return;

    // Check if product already in line items
    if (lineItems.find(item => item.sku === product.sku)) {
      toast.warning('Product already added to this order', {
        description: `SKU: ${product.sku}`
      });
      return;
    }

    // Get supplier cost - Priority: 1) Supplier SKU match, 2) Selected supplier mapping, 3) Preferred supplier
    let supplierCost = selectedOption?.supplierCost || product.supplier_unit_cost || 0;
    let supplierSku = selectedOption?.supplierSku || product.supplier_sku_match || null;
    
    // If no direct match but there's a preferred supplier cost, use it
    if (!supplierCost && product.preferred_supplier_cost) {
      supplierCost = product.preferred_supplier_cost;
    }

    const newItem = {
      id: Date.now(),
      product_id: product.id || product._id,
      sku: product.sku,
      supplier_sku: supplierSku,  // Store supplier SKU for reference
      product_name: product.name || product.product_name,
      quantity: 100,
      weight: '',  // Weight value (decimal)
      weight_unit: 'KG',  // Default unit: KG or LB
      unit_price: supplierCost,  // Auto-fill with supplier cost
      freight_per_unit: 0,
      import_duties_per_unit: 0,
      total: supplierCost * 100  // Pre-calculate total
    };

    setLineItems([...lineItems, newItem]);
    setSelectedProduct(null);
    
    if (supplierSku) {
      toast.success('Product added with supplier pricing', {
        description: `Supplier SKU: ${supplierSku}, Cost: $${supplierCost.toFixed(2)}`
      });
    } else if (supplierCost > 0) {
      toast.success('Product added with preferred supplier pricing', {
        description: `Unit Cost: $${supplierCost.toFixed(2)}`
      });
    }
  };

  // Update line item
  const handleLineItemChange = (id, field, value) => {
    const updated = lineItems.map(item => {
      if (item.id === id) {
        // Handle weight_unit as string, others as numbers (except weight which can be empty string)
        let updatedItem;
        if (field === 'weight_unit') {
          updatedItem = { ...item, [field]: value };
        } else if (field === 'weight') {
          // Allow empty string for weight, otherwise parse as float
          updatedItem = { ...item, [field]: value === '' ? '' : (parseFloat(value) || 0) };
        } else {
          updatedItem = { ...item, [field]: parseFloat(value) || 0 };
        }
        
        // Recalculate total
        updatedItem.total = 
          (updatedItem.quantity * updatedItem.unit_price) +
          (updatedItem.quantity * updatedItem.freight_per_unit) +
          (updatedItem.quantity * updatedItem.import_duties_per_unit);
        
        return updatedItem;
      }
      return item;
    });

    setLineItems(updated);
  };

  // Helper function to convert weight between KG and LB
  const convertWeight = (weight, fromUnit) => {
    if (!weight || weight === '' || isNaN(weight)) return null;
    const numWeight = parseFloat(weight);
    if (fromUnit === 'KG') {
      return (numWeight * 2.20462).toFixed(2); // KG to LB
    } else {
      return (numWeight / 2.20462).toFixed(2); // LB to KG
    }
  };

  // Delete line item
  const handleDeleteLineItem = (id) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  // Calculate totals - Now uses editable PO-level costs (all default to $0)
  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => 
      sum + (item.quantity * item.unit_price), 0
    );
    
    // Line-item level freight (per unit) - NOT used in cost summary display
    const lineFreight = lineItems.reduce((sum, item) => 
      sum + (item.quantity * item.freight_per_unit), 0
    );

    // Line-item level duties (per unit) - NOT used in cost summary display  
    const lineDuties = lineItems.reduce((sum, item) => 
      sum + (item.quantity * item.import_duties_per_unit), 0
    );

    // Use PO-level editable cost fields (all default to $0)
    const intlFreight = poCosts.intl_freight || 0;
    const importDuties = poCosts.import_duties || 0;
    const handling = poCosts.inbound_handling || 0;
    const domestic = poCosts.domestic_freight || 0;
    
    // Total = subtotal + all PO-level costs
    const total = subtotal + intlFreight + importDuties + handling + domestic;

    return { 
      subtotal, 
      freight: intlFreight,      // PO-level Int'l Freight
      duties: importDuties,      // PO-level Import Duties
      handling,                  // PO-level Inbound Handling
      domestic,                  // PO-level Domestic Freight
      total,
      // Keep line-item totals for reference
      lineFreight,
      lineDuties
    };
  };

  const totals = calculateTotals();

  // Save PO
  const handleSave = async (submitForApproval = false) => {
    if (!formData.supplier_id) {
      toast.error('Missing required field', {
        description: 'Please select a supplier'
      });
      return;
    }

    if (lineItems.length === 0) {
      toast.error('Missing required field', {
        description: 'Please add at least one product'
      });
      return;
    }

    if (!formData.expected_delivery) {
      toast.error('Missing required field', {
        description: 'Please set expected delivery date'
      });
      return;
    }

    setLoading(true);

    try {
      const poData = {
        tenant_id: tenantId,
        po_number: formData.po_number,
        vendor_po_number: formData.vendor_po_number || null,  // Supplier's reference number
        supplier_id: formData.supplier_id,
        supplier_name: formData.supplier_name,
        order_date: formData.order_date,
        expected_delivery: formData.expected_delivery,
        warehouse_location: formData.warehouse_location,
        currency: formData.currency,
        payment_terms: formData.payment_terms,
        notes: formData.notes,
        status: submitForApproval ? 'submitted' : 'draft',
        line_items: lineItems.map(item => ({
          product_id: item.product_id,
          sku: item.sku,
          product_name: item.product_name,
          quantity: parseInt(item.quantity) || 0,
          weight: item.weight === '' ? null : (parseFloat(item.weight) || null),
          weight_unit: item.weight_unit || 'KG',
          unit_price: parseFloat(item.unit_price) || 0,
          freight_per_unit: parseFloat(item.freight_per_unit) || 0,
          import_duties_per_unit: parseFloat(item.import_duties_per_unit) || 0,
          total: parseFloat(item.total) || 0
        })),
        cost_breakdown: {
          subtotal: totals.subtotal,
          international_freight: poCosts.intl_freight,
          import_duties: poCosts.import_duties,
          inbound_handling: poCosts.inbound_handling,
          domestic_freight: poCosts.domestic_freight,
          total: totals.total
        },
        // Also store PO-level costs separately for easy editing later
        po_costs: {
          intl_freight: poCosts.intl_freight,
          import_duties: poCosts.import_duties,
          inbound_handling: poCosts.inbound_handling,
          domestic_freight: poCosts.domestic_freight
        }
      };

      console.log('Saving PO:', poData);

      // Use the correct endpoint - api.post returns data directly
      const result = await api.post('/api/purchase-orders', poData, {
        headers: {
          'X-Tenant-ID': tenantId
        }
      });

      console.log('PO saved:', result);
      
      // Upload attachments if any
      if (attachments.length > 0 && result._id) {
        console.log('Uploading attachments for PO:', result._id);
        for (const att of attachments) {
          if (att.file) {
            try {
              const formDataUpload = new FormData();
              formDataUpload.append('file', att.file);
              
              await api.post(`/api/purchase-orders/${result._id}/attachments`, formDataUpload, {
                headers: {
                  'X-Tenant-ID': tenantId,
                  'Content-Type': 'multipart/form-data'
                }
              });
              console.log('Uploaded attachment:', att.filename);
            } catch (uploadError) {
              console.error('Failed to upload attachment:', att.filename, uploadError);
              toast.warning(`Failed to upload attachment: ${att.filename}`);
            }
          }
        }
      }
      
      toast.success(
        submitForApproval ? '🎉 Purchase Order submitted!' : '✅ Purchase Order saved!',
        {
          description: `PO #${formData.po_number} has been ${submitForApproval ? 'submitted for approval' : 'saved as draft'}`
        }
      );
      onNavigate('list');
    } catch (error) {
      console.error('Error saving PO:', error);
      // Show the actual error message
      const errorMessage = error.response?.data?.detail 
        || error.response?.data?.message 
        || error.message 
        || 'Failed to save purchase order';
      toast.error('Failed to save Purchase Order', {
        description: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3 bg-[#0f1729] min-h-screen">
      {/* Compact Header */}
      <div className="mb-3">
        <Button 
          variant="ghost"
          onClick={() => onNavigate('list')}
          className="mb-2 h-7 px-2 text-xs text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-3 h-3 mr-1" />
          Back
        </Button>
        <h1 className="text-lg font-semibold text-white">
          Create Purchase Order
        </h1>
      </div>

      {/* PO Header Section - Compact */}
      <div className="bg-[#1a2744] rounded-lg p-3 mb-3">
        <h2 className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-3">Purchase Order Details</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">PO Number</label>
            <Input value={formData.po_number} disabled className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-gray-400" />
          </div>
          
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Vendor PO #</label>
            <Input 
              value={formData.vendor_po_number}
              onChange={(e) => setFormData({...formData, vendor_po_number: e.target.value})}
              placeholder="Supplier's ref #"
              className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-white placeholder:text-gray-500"
            />
          </div>
          
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Supplier *</label>
            <select
              value={formData.supplier_id}
              onChange={(e) => {
                const supplier = suppliers.find(s => s._id === e.target.value);
                setFormData({
                  ...formData, 
                  supplier_id: e.target.value,
                  supplier_name: supplier?.name || ''
                });
              }}
              className="w-full h-8 py-1 px-2 text-sm bg-[#0f1729] border border-gray-700 text-white rounded focus:outline-none focus:border-blue-500"
            >
              <option value="">Select supplier...</option>
              {suppliers.map(supplier => (
                <option key={supplier._id} value={supplier._id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Order Date</label>
            <Input 
              type="date" 
              value={formData.order_date}
              onChange={(e) => setFormData({...formData, order_date: e.target.value})}
              className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-white"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Expected Delivery *</label>
            <Input 
              type="date" 
              value={formData.expected_delivery}
              onChange={(e) => setFormData({...formData, expected_delivery: e.target.value})}
              min={formData.order_date}
              className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-white"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Warehouse</label>
            <Input 
              value={formData.warehouse_location}
              onChange={(e) => setFormData({...formData, warehouse_location: e.target.value})}
              className="h-8 py-1 px-2 text-sm bg-[#0f1729] border-gray-700 text-white"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Currency</label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData({...formData, currency: e.target.value})}
              className="w-full h-8 py-1 px-2 text-sm bg-[#0f1729] border border-gray-700 text-white rounded focus:outline-none focus:border-blue-500"
            >
              <option value="USD">USD</option>
              <option value="INR">INR</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Payment Terms</label>
            <select
              value={formData.payment_terms}
              onChange={(e) => setFormData({...formData, payment_terms: e.target.value})}
              className="w-full h-8 py-1 px-2 text-sm bg-[#0f1729] border border-gray-700 text-white rounded focus:outline-none focus:border-blue-500"
            >
              <option value="Net 30">Net 30</option>
              <option value="Net 60">Net 60</option>
              <option value="Net 90">Net 90</option>
              <option value="COD">COD</option>
              <option value="Advance">Advance</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-medium block mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              className="w-full h-8 py-1 px-2 text-sm bg-[#0f1729] border border-gray-700 text-white rounded focus:outline-none focus:border-blue-500"
            >
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
            </select>
          </div>
        </div>
      </div>

      {/* Line Items Section - Compact */}
      <div className="bg-[#1a2744] rounded-lg p-3 mb-3">
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">
            Line Items ({lineItems.length})
          </h2>
        </div>
        
        {/* Product Selection - Searchable Dropdown */}
        <div className="flex gap-2 mb-3">
          <div className="flex-1">
            <AsyncSelect
              cacheOptions
              defaultOptions={productOptions}
              loadOptions={loadProductOptions}
              value={selectedProduct}
              onChange={(option) => setSelectedProduct(option)}
              isSearchable={true}
              isClearable={true}
              placeholder={formData.supplier_id 
                ? "Search by SKU, name, or supplier SKU..." 
                : "Search by SKU or product name..."}
              styles={selectStyles}
              noOptionsMessage={({ inputValue }) => 
                inputValue ? "No products found - try supplier SKU" : "Type to search..."
              }
              loadingMessage={() => "Searching..."}
            />
          </div>
          <Button 
            onClick={() => {
              if (selectedProduct) {
                handleAddProduct(selectedProduct.value, selectedProduct);
                setSelectedProduct(null);
              }
            }} 
            disabled={!selectedProduct} 
            className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        </div>

        {/* Line Items Table - Compact */}
        {lineItems.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#0f1729]">
                <tr>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium">SKU</th>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium">Sup. SKU</th>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium">Product</th>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium w-16">Qty</th>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium w-28">Weight</th>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium w-20">Unit $</th>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium w-20">Freight</th>
                  <th className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider text-gray-400 font-medium w-20">Duties</th>
                  <th className="px-2 py-1.5 text-right text-[10px] uppercase tracking-wider text-gray-400 font-medium w-20">Total</th>
                  <th className="px-2 py-1.5 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {lineItems.map((item) => (
                  <tr key={item.id} className="hover:bg-[#0f1729]/50">
                    <td className="px-2 py-1.5 text-white text-sm">{item.sku}</td>
                    <td className="px-2 py-1.5 text-orange-400 text-sm">
                      {item.supplier_sku || '—'}
                    </td>
                    <td className="px-2 py-1.5 text-gray-300 text-sm truncate max-w-[150px]">{item.product_name}</td>
                    <td className="px-2 py-1.5">
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(item.id, 'quantity', e.target.value)}
                        className="w-14 h-6 py-0.5 px-1 text-xs bg-[#0f1729] border-gray-700 text-white"
                        min="1"
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={item.weight}
                            onChange={(e) => handleLineItemChange(item.id, 'weight', e.target.value)}
                            className="w-14 h-6 py-0.5 px-1 text-xs bg-[#0f1729] border-gray-700 text-white"
                            min="0"
                            step="0.01"
                            placeholder="0"
                            data-testid={`weight-input-${item.id}`}
                          />
                          <select
                            value={item.weight_unit}
                            onChange={(e) => handleLineItemChange(item.id, 'weight_unit', e.target.value)}
                            className="h-6 py-0 px-1 text-xs bg-[#0f1729] border border-gray-700 text-white rounded focus:outline-none focus:border-blue-500"
                            data-testid={`weight-unit-${item.id}`}
                          >
                            <option value="KG">KG</option>
                            <option value="LB">LB</option>
                          </select>
                        </div>
                        {item.weight && item.weight !== '' && (
                          <span className="text-[10px] text-gray-500 mt-0.5">
                            ({convertWeight(item.weight, item.weight_unit)} {item.weight_unit === 'KG' ? 'lb' : 'kg'})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => handleLineItemChange(item.id, 'unit_price', e.target.value)}
                        className="w-16 h-6 py-0.5 px-1 text-xs bg-[#0f1729] border-gray-700 text-white"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        type="number"
                        value={item.freight_per_unit}
                        onChange={(e) => handleLineItemChange(item.id, 'freight_per_unit', e.target.value)}
                        className="w-16 h-6 py-0.5 px-1 text-xs bg-[#0f1729] border-gray-700 text-white"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input
                        type="number"
                        value={item.import_duties_per_unit}
                        onChange={(e) => handleLineItemChange(item.id, 'import_duties_per_unit', e.target.value)}
                        className="w-16 h-6 py-0.5 px-1 text-xs bg-[#0f1729] border-gray-700 text-white"
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold text-emerald-400 text-sm">
                      ${item.total.toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5">
                      <button 
                        onClick={() => handleDeleteLineItem(item.id)}
                        className="p-1 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-[#0f1729] border border-gray-700 rounded p-3 text-center">
            <p className="text-gray-400 text-xs">
              No products added. Select products above to add to this PO.
            </p>
          </div>
        )}
      </div>

      {/* Two-column layout for Cost Summary and Notes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        {/* Cost Summary - Now with EDITABLE cost fields */}
        <div className="bg-[#1a2744] rounded-lg p-3">
          <h2 className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Cost Summary</h2>
          <div className="space-y-1">
            {/* Subtotal - Read-only (calculated from line items) */}
            <div className="flex justify-between items-center py-1 text-sm">
              <span className="text-gray-400">Subtotal:</span>
              <span className="text-white font-medium">${totals.subtotal.toFixed(2)}</span>
            </div>
            
            {/* Int'l Freight - EDITABLE */}
            <div className="flex justify-between items-center py-1 text-sm">
              <span className="text-gray-400">Int&apos;l Freight:</span>
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={poCosts.intl_freight}
                  onChange={(e) => handlePoCostChange('intl_freight', e.target.value)}
                  className="w-20 h-6 py-0 px-1.5 text-xs text-right bg-[#0f1729] border-gray-700 text-white"
                  data-testid="po-intl-freight-input"
                />
              </div>
            </div>
            
            {/* Import Duties - EDITABLE */}
            <div className="flex justify-between items-center py-1 text-sm">
              <span className="text-gray-400">Import Duties:</span>
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={poCosts.import_duties}
                  onChange={(e) => handlePoCostChange('import_duties', e.target.value)}
                  className="w-20 h-6 py-0 px-1.5 text-xs text-right bg-[#0f1729] border-gray-700 text-white"
                  data-testid="po-import-duties-input"
                />
              </div>
            </div>
            
            {/* Inbound Handling - EDITABLE (was hardcoded $200) */}
            <div className="flex justify-between items-center py-1 text-sm">
              <span className="text-gray-400">Inbound Handling:</span>
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={poCosts.inbound_handling}
                  onChange={(e) => handlePoCostChange('inbound_handling', e.target.value)}
                  className="w-20 h-6 py-0 px-1.5 text-xs text-right bg-[#0f1729] border-gray-700 text-white"
                  data-testid="po-inbound-handling-input"
                />
              </div>
            </div>
            
            {/* Domestic Freight - EDITABLE (was hardcoded $100) */}
            <div className="flex justify-between items-center py-1 text-sm">
              <span className="text-gray-400">Domestic Freight:</span>
              <div className="flex items-center">
                <span className="text-gray-500 mr-1">$</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={poCosts.domestic_freight}
                  onChange={(e) => handlePoCostChange('domestic_freight', e.target.value)}
                  className="w-20 h-6 py-0 px-1.5 text-xs text-right bg-[#0f1729] border-gray-700 text-white"
                  data-testid="po-domestic-freight-input"
                />
              </div>
            </div>
            
            <div className="border-t border-gray-700 my-1"></div>
            <div className="flex justify-between py-1">
              <span className="text-sm font-semibold text-white">Total:</span>
              <span className="text-base font-bold text-emerald-400">${totals.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Notes - Compact */}
        <div className="bg-[#1a2744] rounded-lg p-3">
          <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold block mb-2">Notes (Optional)</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            placeholder="Add notes..."
            className="w-full p-2 text-sm border border-gray-700 rounded bg-[#0f1729] text-white placeholder-gray-500 resize-none"
            rows="4"
          />
        </div>
        
        {/* Attachments Section */}
        <div className="bg-[#1a2744] rounded-lg p-3">
          <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold block mb-2">
            <Paperclip className="w-3 h-3 inline mr-1" />
            Attachments
          </label>
          
          {/* Upload Area */}
          <div 
            className="border-2 border-dashed border-gray-600 rounded-lg p-4 text-center hover:border-blue-500 transition-colors cursor-pointer"
            onClick={() => document.getElementById('po-attachment-input').click()}
          >
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                
                // Validate file size (10MB max)
                if (file.size > 10 * 1024 * 1024) {
                  toast.error('File too large', { description: 'Maximum file size is 10MB' });
                  return;
                }
                
                setUploadingFile(true);
                try {
                  // For now, just store file info locally (will be uploaded with PO save)
                  const newAttachment = {
                    file_id: `att-${Date.now()}`,
                    filename: file.name,
                    file_type: file.type,
                    file_size: file.size,
                    file: file, // Store the actual file for upload later
                    uploaded_at: new Date().toISOString()
                  };
                  setAttachments([...attachments, newAttachment]);
                  toast.success('File added', { description: file.name });
                } catch (error) {
                  toast.error('Failed to add file');
                } finally {
                  setUploadingFile(false);
                  e.target.value = ''; // Reset input
                }
              }}
              className="hidden"
              id="po-attachment-input"
            />
            {uploadingFile ? (
              <span className="text-gray-400">Uploading...</span>
            ) : (
              <>
                <span className="text-blue-400">Click to upload</span>
                <span className="text-gray-500"> or drag and drop</span>
                <p className="text-gray-600 text-xs mt-1">PDF, JPG, PNG up to 10MB</p>
              </>
            )}
          </div>
          
          {/* List of Attachments */}
          {attachments.length > 0 && (
            <div className="mt-3 space-y-2">
              {attachments.map(att => (
                <div 
                  key={att.file_id} 
                  className="flex items-center bg-[#0f1729] p-2 rounded border border-gray-700"
                >
                  <FileText className="w-4 h-4 text-red-400 mr-2" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm truncate">{att.filename}</p>
                    <p className="text-gray-500 text-xs">
                      {(att.file_size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button 
                    onClick={() => setAttachments(attachments.filter(a => a.file_id !== att.file_id))}
                    className="p-1 text-red-400 hover:text-red-300 ml-2"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions - Compact */}
      <div className="flex gap-2 justify-end">
        <Button 
          variant="outline"
          onClick={() => onNavigate('list')}
          className="h-8 px-3 text-xs border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          Cancel
        </Button>
        <Button 
          variant="outline"
          onClick={() => handleSave(false)}
          disabled={loading}
          className="h-8 px-3 text-xs border-gray-600 text-gray-300 hover:bg-gray-700"
        >
          <Save className="w-3 h-3 mr-1" />
          Save Draft
        </Button>
        <Button 
          onClick={() => handleSave(true)}
          disabled={loading}
          className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700"
        >
          <Send className="w-3 h-3 mr-1" />
          Submit
        </Button>
      </div>
    </div>
  );
};

export default PurchaseOrderCreate;
